import { useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { v4 as uuidv4 } from 'uuid';

import { Criterion, Field, FieldType, Form, Quota, SelectChoiceField } from '@app/api';
import { isUUID, RemovableCustomFormField } from 'components';
import { AddButton } from '@app/form';
import { useFieldConfigurations } from '../fieldConfigurations';
import { TooltipText } from '@app/core';
import { useLogicOptions } from './lib/getLogicOptions';
import { getQuestionNumber } from './lib/getQuestionNumber';
import { DuplicateFormSession } from '../components/DuplicateFormSession';
import { cleanLogicForAllFields } from './lib/cleanLogicForAllFields';
import { cleanLogicForField } from './lib/cleanLogicForField';
import { createCriterionForField } from 'lib/criteria';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  buttonsContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.marginBase * 3,
    marginRight: -theme.marginBase,
    '& > *': {
      marginRight: theme.marginBase,
    },
  },
  addButtonContainer: {
    flex: 1,
  },
}));

interface Props {
  form?: Partial<Form>;
  onChange: (form?: Partial<Form>) => any;
  criteria?: Criterion[];
  onCriteriaChange?: (criteria: Criterion[]) => any;
  maxNumberOfQuestions?: number;
  sessionId?: string;
  numberOfParticipantRequired?: number;
  quotas?: Quota[];
  onQuotaChange?: (quotas: Quota[]) => any;
}

export const CustomFormBuilder = ({
  form,
  onChange,
  criteria,
  onCriteriaChange,
  maxNumberOfQuestions,
  sessionId,
  numberOfParticipantRequired,
  quotas,
  onQuotaChange,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const fields = (form && form.fields) || [];
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const numberOfQuestions = fields.length;
  const hasReachMaxQuestions =
    maxNumberOfQuestions !== undefined &&
    maxNumberOfQuestions >= 0 &&
    numberOfQuestions >= maxNumberOfQuestions;
  const fieldConfigurations = useFieldConfigurations();

  const onFieldsChange = useCallback(
    (fields: Field[]) => {
      let newForm = form
        ? { ...form }
        : {
            id: uuidv4(),
            fields: [],
          };
      newForm.fields = cleanLogicForAllFields(fields);

      onChange(newForm);
    },
    [form, onChange],
  );

  const onFieldChange = useCallback(
    (field: Field, index: number) => {
      if (!form?.fields) {
        return;
      }
      const newFields = [...form.fields];
      newFields[index] = cleanLogicForField(field);
      onFieldsChange(newFields);
    },
    [form, onFieldsChange],
  );

  const onDelete = useCallback(
    (index: number) => {
      const ref = fields[index] && fields[index].ref;
      const newFields = fields.filter((_: any, i: number) => i !== index);

      if (onCriteriaChange && ref && criteria) {
        onCriteriaChange(criteria.filter((c) => c.fieldRef !== ref));
      }
      onFieldsChange(newFields);
    },
    [fields, onCriteriaChange, criteria, form, onFieldsChange],
  );

  const removeCriterion = useCallback(
    (criterion: Criterion) => {
      if (onCriteriaChange && criteria) {
        onCriteriaChange(criteria.filter((c) => c !== criterion));
      }
    },
    [criteria, onCriteriaChange],
  );

  const onMoveUp = useCallback(
    (index: number) => {
      const field = fields[index];
      const newFields = fields.filter((_: any, i: number) => i !== index);
      newFields.splice(index - 1, 0, field);
      onFieldsChange(newFields);
    },
    [onFieldsChange, fields],
  );

  const onMoveDown = useCallback(
    (index: number) => {
      const field = fields[index];
      const newFields = fields.filter((_: any, i: number) => i !== index);
      newFields.splice(index + 1, 0, field);
      onFieldsChange(newFields);
    },
    [onFieldsChange, fields],
  );

  const onDuplicate = useCallback(
    (index: number) => {
      const field = fields[index];
      const copy: Field = {
        ...field,
        id: uuidv4(),
        ref: uuidv4(),
        jumpActions: [],
      };
      if (
        (field.type === FieldType.SINGLE_CHOICE ||
          field.type === FieldType.MULTIPLE_CHOICE) &&
        field.properties.choices
      ) {
        (copy as SelectChoiceField).properties = {
          ...field.properties,
          choices: field.properties.choices.map((choice) => ({
            ...choice,
            ref: isUUID(choice.ref) ? uuidv4() : choice.ref,
          })),
        };
      }
      const fieldsCopy = [...fields];
      fieldsCopy.splice(index + 1, 0, copy);
      onFieldsChange(fieldsCopy);
    },
    [fields, onFieldsChange],
  );

  const onCriterionChange = useCallback(
    (criterion: Criterion) => {
      if (!onCriteriaChange) {
        return;
      }
      const newCriteria = criteria ? [...criteria] : [];
      const index = newCriteria.findIndex(
        (c) => c.fieldRef === criterion.fieldRef || c.id === criterion.id,
      );

      if (index !== -1) {
        newCriteria[index] = criterion;
      } else {
        newCriteria.push(criterion);
      }
      onCriteriaChange(newCriteria);
    },
    [onCriteriaChange, criteria],
  );

  const onAddClick = useCallback(() => {
    const createDefaultProperties =
      fieldConfigurations[FieldType.SINGLE_CHOICE].createDefaultProperties;
    const newOption = {
      id: uuidv4(),
      title: '',
      type: FieldType.SINGLE_CHOICE,
      ref: uuidv4(),
      properties: createDefaultProperties
        ? createDefaultProperties()
        : undefined,
      validation: {
        required: true,
      },
    };
    onFieldsChange([...fields, newOption]);
    onCriterionChange(createCriterionForField(newOption, true));
    setSelected(newOption.ref);
  }, [
    fields,
    setSelected,
    form,
    onFieldsChange,
    onCriterionChange,
    fieldConfigurations,
  ]);

  const onSelectClick = useCallback(
    (ref: string | undefined) => {
      if (!ref) {
        setSelected(undefined);
      }
      const field = fields.find((f) => f.ref === ref);
      if (!field) {
        return;
      }
      setSelected(ref);
    },
    [fields],
  );

  const logicJumpOptions = useLogicOptions(fields);

  return (
    <>
      <DuplicateFormSession
        onChange={onChange}
        form={form}
        sessionId={sessionId}
        onCriteriaChange={onCriteriaChange}
        criteria={criteria}
      />
      {fields.map((field, index) => {
        const fieldNumber = getQuestionNumber(fields, field);
        return (
          <RemovableCustomFormField
            sessionId={sessionId}
            field={field}
            key={field.id || field.ref}
            selected={selected === field.ref}
            onSelect={onSelectClick}
            index={index}
            onChange={onFieldChange}
            onDelete={onDelete}
            onMoveUp={index > 0 ? onMoveUp : undefined}
            onMoveDown={index < fields.length - 1 ? onMoveDown : undefined}
            onDuplicate={onDuplicate}
            criterion={
              criteria && criteria.find((c) => c.fieldRef === field.ref)
            }
            onCriterionChange={onCriteriaChange ? onCriterionChange : undefined}
            onRemoveCriterion={removeCriterion}
            logicJumpOptions={logicJumpOptions}
            fieldNumber={fieldNumber > 0 ? fieldNumber : undefined}
            isLastField={index === fields.length - 1}
            numberOfParticipantRequired={numberOfParticipantRequired}
            quotas={quotas}
            onQuotaChange={onQuotaChange}
          />
        );
      })}
      <div className={classes.buttonsContainer}>
        <TooltipText
          title={
            hasReachMaxQuestions
              ? t('screeningForm.create.questionBuilder.maxNumberOfQuestions', {
                  count: maxNumberOfQuestions,
                })
              : undefined
          }
        >
          <div className={classes.addButtonContainer}>
            <AddButton
              text={t('screeningForm.create.questionBuilder.addQuestion')}
              disabled={hasReachMaxQuestions}
              onClick={onAddClick}
            />
          </div>
        </TooltipText>
      </div>
    </>
  );
};
